import { type Editor, Path, Range, Text, Node, type Point } from "slate";

/**
 * A rule that has children is stored as a connector whose first child is the
 * parent rule. Indent must target that connector, not the inner rule, or the
 * connector normaliser unwraps the one-child wrap and the indent is a no-op.
 */
export const INDENT_CONNECTOR_TYPE = "connector";

const toElementPoint = (editor: Pick<Editor, "children">, point: Point): Point => {
  const path = point.path;
  const node = Node.get(editor, path);
  if (Text.isText(node)) {
    return { path: Path.parent(path), offset: 0 };
  }
  return { path, offset: 0 };
};

const forwardRange = (range: Range): Range => {
  if (Range.isBackward(range)) {
    return { anchor: range.focus, focus: range.anchor };
  }
  return range;
};

/** Lift a point that sits on the parent rule of a connector up to that connector. */
export const expandIndentRange = (editor: Pick<Editor, "children">, range: Range): Range => {
  const lift = (path: Path): Path => {
    let current = path;
    while (current.length > 0 && current[current.length - 1] === 0) {
      const parentPath = Path.parent(current);
      let parent: Node;
      try {
        parent = Node.get(editor, parentPath);
      } catch {
        break;
      }
      if ((parent as { type?: string }).type !== INDENT_CONNECTOR_TYPE) break;
      current = parentPath;
    }
    return current;
  };

  let anchorPath = lift(range.anchor.path);
  let focusPath = lift(range.focus.path);
  if (Path.isAncestor(anchorPath, focusPath)) {
    focusPath = anchorPath;
  } else if (Path.isAncestor(focusPath, anchorPath)) {
    anchorPath = focusPath;
  }

  return {
    anchor: { path: anchorPath, offset: 0 },
    focus: { path: focusPath, offset: 0 },
  };
};

export type IndentPlan = { type: "move"; at: Path | Range; to: Path } | { type: "wrap"; at: Range };

export const planIndent = (editor: Pick<Editor, "children">, input: Path | Range): IndentPlan => {
  const raw = Range.isRange(input)
    ? input
    : {
        anchor: { path: input, offset: 0 },
        focus: { path: input, offset: 0 },
      };
  const elementRange = forwardRange({
    anchor: toElementPoint(editor, forwardRange(raw).anchor),
    focus: toElementPoint(editor, forwardRange(raw).focus),
  });
  const range = expandIndentRange(editor, elementRange);

  try {
    const prevPath = Path.previous(range.anchor.path);
    const prev = Node.get(editor, prevPath);
    if ((prev as { type?: string }).type === INDENT_CONNECTOR_TYPE) {
      const at = Path.equals(range.anchor.path, range.focus.path) ? range.anchor.path : range;
      const children = (prev as { children?: unknown[] }).children ?? [];
      return { type: "move", at, to: [...prevPath, children.length] };
    }
  } catch {
    // first sibling has nothing to indent under
  }

  let at = range;
  try {
    at = {
      anchor: { path: Path.previous(range.anchor.path), offset: 0 },
      focus: { path: range.focus.path, offset: 0 },
    };
  } catch {
    // keep the selection itself when there is no previous sibling
  }
  return { type: "wrap", at };
};
